import { createThemeRegistry } from '@splunk/visualization-themes/helper';
import { AST, Expr, Method, OptionsStanza, OptionsAST, ParserSymbol } from './AST';
import { Encoding } from './Encoding';

export const expr0: ParserSymbol = { type: 'identifier', v: 'WOPR' };

export const expr1: Method = {
    type: 'method',
    name: 'print',
    args: [
        { type: 'string', v: 'how' },
        { type: 'string', v: 'about' },
        { type: 'string', v: 'a' },
        { type: 'string', v: 'nice' },
        { type: 'string', v: 'game' },
    ],
};

export const expr2: Method = {
    type: 'method',
    name: 'println',
    args: [
        { type: 'string', v: 'OF' },
        { type: 'string', v: 'CHESS' },
    ],
};

export const valueExprs1: Expr[] = [expr0, expr1, expr2]; // WOPR|print('how','about','a','nice','game')|println('OF','CHESS')

export const chessDSL = "WOPR|print('how','about','a','nice','game')|println('OF','CHESS')";
export const defconDSL = "WOPR|defenseCondition(0)|prefix('xxx')|rangeValue(ranges)|matchValue(matches)";

export const ast: AST = {
    context: {
        defconColors: {
            colors: ['red', 'orange', 'yellow', 'blue', 'green'],
        },
    },
    dimensions: {
        warGames: {
            value: valueExprs1,
        },
        conclusion: {
            value: [
                {
                    type: 'identifier',
                    v: 'WOPR',
                },
                {
                    type: 'method',
                    name: 'defenseCondition',
                    args: [{ type: 'number', v: 0 }],
                },
                {
                    type: 'method',
                    name: 'prefix',
                    args: [{ type: 'string', v: 'xxx' }],
                },
                {
                    type: 'method',
                    name: 'rangeValue',
                    args: [{ type: 'identifier', v: 'ranges' }],
                },
                {
                    type: 'method',
                    name: 'matchValue',
                    args: [{ type: 'identifier', v: 'matches' }],
                },
            ],
        },
    },
};

export const dslEncoding: Encoding = {
    context: {
        defconColors: {
            colors: ['red', 'orange', 'yellow', 'blue', 'green'],
        },
    },
    dimensions: {
        warGames: {
            value: chessDSL,
        },
        conclusion: {
            value: defconDSL,
        },
    },
};

export const dynamicOptions: OptionsStanza = {
    options: {
        aNumber: 42,
        aString: 'a string',
        aColor: '#FF0000',
        aTime: new Date('Thu Jul 16 2020 19:47:12 GMT-0700 (PDT)'),
        expression1: '  > `aString`',
        expression2: '>primary|last() ',
        expression3: `>${chessDSL}`,
        expression4: `>${defconDSL}`,
    },
    context: {
        foo: 'FOO',
        bar: 'BAR',
        nest: {
            nestFoo: 'NESTFOO',
            nestBar: 'NESTBAR',
        },
    },
};

export const optionsAST: OptionsAST = {
    simpleOptions: {
        aNumber: 42,
        aString: 'a string',
        aColor: '#FF0000',
        aTime: new Date('Thu Jul 16 2020 19:47:12 GMT-0700 (PDT)'),
    },
    expressions: {
        expression1: [{ type: 'string', v: 'aString' }],
        expression2: [
            { type: 'identifier', v: 'primary' },
            { type: 'method', name: 'last', args: [] },
        ],
        expression3: valueExprs1,
        expression4: [
            {
                type: 'identifier',
                v: 'WOPR',
            },
            {
                type: 'method',
                name: 'defenseCondition',
                args: [{ type: 'number', v: 0 }],
            },
            {
                type: 'method',
                name: 'prefix',
                args: [{ type: 'string', v: 'xxx' }],
            },
            {
                type: 'method',
                name: 'rangeValue',
                args: [{ type: 'identifier', v: 'ranges' }],
            },
            {
                type: 'method',
                name: 'matchValue',
                args: [{ type: 'identifier', v: 'matches' }],
            },
        ],
    },
    context: {
        foo: 'FOO',
        bar: 'BAR',
        nest: {
            nestFoo: 'NESTFOO',
            nestBar: 'NESTBAR',
        },
    },
};

export const context = {
    majorThreshold: [
        {
            from: 3,
            value: '#A52A2A',
        },
        {
            from: 2,
            to: 3,
            value: '#FFFF00',
        },
        {
            from: 1,
            to: 2,
            value: '#800080',
        },
        {
            to: 1,
            value: '#0000FF',
        },
    ],
    deltaThreshold: [
        {
            from: 3,
            value: 'white',
        },
        {
            from: 2,
            to: 3,
            value: 'brown',
        },
        {
            from: 1,
            to: 2,
            value: 'gray',
        },
        {
            to: 1,
            value: 'black',
        },
    ],
    backgroundThreshold: [
        {
            from: 3,
            value: 'yellow',
        },
        {
            from: 2,
            to: 3,
            value: 'green',
        },
        {
            from: 1,
            to: 2,
            value: 'blue',
        },
        {
            to: 1,
            value: 'red',
        },
    ],
};

export const svDynamicOptions: OptionsStanza = {
    context,
    options: {
        sparkline: "> primary|seriesByType('number')",
        major: '> sparkline|lastPoint()',
        majorColor: '> major|rangeValue(majorThreshold)',
        delta: '> sparkline|delta(-2)',
        deltaColor: '> delta|rangeValue(deltaThreshold)',
        backgroundColor: '> delta|rangeValue(backgroundThreshold)',
        notBasedOnData: '>42',
        xColor: '>themes.x',
        yColor: '>themes.y',
        zColor: 'blue',
        aNumber: 42,
        aColor: '>2|rangeValue(majorThreshold)',
        aFormattedTheme: '>themes.aNumber|rangeValue(majorThreshold)',
        singleSeries: '> seriesByIndex(0)',
        multiSeries: '> frameBySeriesIndexRange(0, 2)',
        singleSeriesFieldName: '>singleSeries|getField()',
        multiSeriesFieldName: '>multiSeries|getField()',
    },
};

export const svDynamicOptions2: OptionsStanza = {
    context,
    options: {
        meh: "> primary | prefix('$')",
        sparkline: "> primary | seriesByType('string') | prefix('$')",
        major: "> primary | seriesByType('string') | lastPoint()",
    },
};

export const testThemeRegistry = createThemeRegistry('@splunk/foobar', 'BazViz');
testThemeRegistry.addDefaultTheme('enterprise', {
    x: 'red',
    y: 'green',
    aNumber: 2,
});
